import { useSearchParams } from "react-router-dom";
import { EnvelopeSimple } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { usePageMeta } from "@/lib/usePageMeta";
import { SiteHeader6 } from "@/components/layout/SiteHeader6";
import { SiteFooter6 } from "@/components/layout/SiteFooter6";
import { InteriorHero } from "@/components/common/InteriorHero";
import { Box6 } from "@/components/common/Box6";
import { Eyebrow6 } from "@/components/common/Eyebrow6";
import { Cta6 } from "@/components/common/Cta6";
import { ProcessSteps6 } from "@/components/sections6/ProcessSteps6";
import { FormField } from "@/components/form/FormField";
import { FormSelect } from "@/components/form/FormSelect";
import { FormTextarea } from "@/components/form/FormTextarea";
import { inquiry6, serviceAreas6, contactMethods6, brand6, process6 } from "@/brand/contentF6";

/**
 * ContactPage — qualifying consultation form. The four-step Process module
 * is reiterated directly above the form; "Write us directly" points to the
 * firm's catch-all inbox, never a personal address.
 */
export default function ContactPage() {
  const [params] = useSearchParams();
  const presetArea = params.get("area") === "speaking" ? "Speaking & Keynotes" : "";
  usePageMeta({
    title: presetArea ? "Speaking inquiry" : "Book a consultation",
    description: inquiry6.intro,
  });

  return (
    <main data-testid="contact-page" style={{ background: C.surfaceLight, color: C.textOnLight }}>
      <SiteHeader6 />

      <InteriorHero
        breadcrumb={[{ label: "Faith 1st Consulting", href: "/" }, { label: "Contact" }]}
        eyebrow={inquiry6.eyebrow}
        title={inquiry6.headline}
        intro={inquiry6.intro}
      />

      {/* Process reiterated */}
      <section data-testid="contact-process" style={{ background: C.surfaceLight }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="mb-8">
            <Eyebrow6 className="mb-4">{inquiry6.processEyebrow}</Eyebrow6>
            <h2 className="font-cormorant" style={{ color: C.textOnLight, fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)", fontWeight: 400, lineHeight: 1.1 }}>
              {process6.headline}
            </h2>
          </div>
          <ProcessSteps6 tone="parchment" testId="contact-process-grid" />
        </div>
      </section>

      {/* Form + side boxes */}
      <section data-testid="contact-body" style={{ background: C.surfaceLight2, borderTop: `1px solid ${C.brassSoft}` }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            <Box6 tone="white" radius="card" className="lg:col-span-8 min-w-0 p-6 sm:p-8 lg:p-12">
              <form data-testid="contact-form" className="space-y-8" onSubmit={(e) => e.preventDefault()}>
                <p
                  className="font-inter text-[10px] uppercase font-semibold pb-4"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em", borderBottom: `1px solid ${C.brassSoft}` }}
                >
                  {inquiry6.formEyebrow}
                </p>

                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                  <FormField label="Full name" name="name" required index="01" testId="field-name" />
                  <FormField label="Organization" name="organization" required index="02" testId="field-organization" />
                </div>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                  <FormField label="Role / title" name="role" required index="03" testId="field-role" />
                  <FormField label="Email" name="email" type="email" required index="04" testId="field-email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                  <FormField label="Phone (optional)" name="phone" type="tel" index="05" testId="field-phone" />
                  <FormSelect label="Preferred contact method" name="contactMethod" options={contactMethods6} required index="06" testId="field-contact-method" />
                </div>
                <FormSelect
                  label="Service area that best fits your situation"
                  name="serviceArea"
                  options={serviceAreas6}
                  required
                  index="07"
                  testId="field-service-area"
                  defaultValue={presetArea}
                />
                <FormTextarea
                  label="What is happening — at a high level"
                  name="description"
                  hint="Two or three sentences is enough. Please leave out names and sensitive incident detail — those belong in the first conversation."
                  required
                  index="08"
                  testId="field-description"
                />

                <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-5">
                  <Cta6 type="submit" testId="contact-submit" wrap className="px-7 py-4 w-full sm:w-auto">
                    Submit confidential inquiry
                  </Cta6>
                  <p className="font-cormorant italic" style={{ color: C.inkOnLightSoft, fontSize: "0.92rem" }}>
                    Reviewed personally. Reply within two business days.
                  </p>
                </div>
              </form>
            </Box6>

            <div className="lg:col-span-4 min-w-0 grid gap-5">
              <Box6 tone="blue" radius="resource" className="p-6 sm:p-8" data-testid="contact-write-directly">
                <p
                  className="font-inter text-[11px] uppercase font-semibold pb-4 mb-5"
                  style={{ color: C.accentMetal, letterSpacing: "0.26em", borderBottom: `1px solid ${C.brassSoft}` }}
                >
                  {inquiry6.writeDirectly.label}
                </p>
                <p className="font-inter leading-[1.65]" style={{ color: C.whiteOnDark, fontSize: "0.92rem" }}>
                  {inquiry6.writeDirectly.body}
                </p>
                <a
                  href={`mailto:${brand6.email}`}
                  data-testid="contact-email-link"
                  className="mt-6 inline-flex items-center gap-2.5 font-inter text-[13px] font-medium break-all transition-colors"
                  style={{ color: C.textOnDark }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = C.accentMetal)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = C.textOnDark)}
                >
                  <EnvelopeSimple size={16} weight="regular" style={{ color: C.accentMetal }} />
                  {brand6.email}
                </a>
              </Box6>

              <Box6 tone="white" radius="resource" className="p-6 sm:p-8" data-testid="contact-assurances">
                <p
                  className="font-inter text-[11px] uppercase font-semibold pb-4 mb-6"
                  style={{ color: C.accentMetal, letterSpacing: "0.26em", borderBottom: `1px solid ${C.brassSoft}` }}
                >
                  How inquiries are held
                </p>
                <ul className="space-y-6">
                  {inquiry6.assurances.map((a, i) => (
                    <li key={a.n} data-testid={`contact-assurance-${i}`} className="flex items-baseline gap-4">
                      <span className="font-cormorant tabular-nums shrink-0" style={{ color: C.accentMetal, fontSize: "1.1rem", lineHeight: 1 }}>
                        {a.n}
                      </span>
                      <div>
                        <p className="font-inter text-[10.5px] uppercase font-semibold mb-1.5" style={{ color: C.textOnLight, letterSpacing: "0.2em" }}>
                          {a.label}
                        </p>
                        <p className="font-inter leading-[1.55]" style={{ color: C.inkOnLight, fontSize: "0.88rem" }}>
                          {a.body}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </Box6>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter6 />
    </main>
  );
}
